/**
 * Recording Manager
 *
 * Captures player inputs during song playback to create charts
 */

import { NoteData } from '../gameplay/Note';

export interface RecordedNote {
  time: number;
  lane: number;
}

export class RecordingManager {
  private recordedNotes: RecordedNote[] = [];
  private isRecording: boolean = false;

  /**
   * Start recording inputs
   */
  startRecording(): void {
    this.recordedNotes = [];
    this.isRecording = true;
    console.log('🔴 Recording started - tap along to the beat!');
  }

  /**
   * Stop recording and return the notes
   */
  stopRecording(): RecordedNote[] {
    this.isRecording = false;
    console.log(`🛑 Recording stopped - captured ${this.recordedNotes.length} notes`);
    return this.recordedNotes;
  }

  /**
   * Record a note at the current time
   */
  recordNote(currentTime: number, lane: number): void {
    if (!this.isRecording) return;

    this.recordedNotes.push({
      time: Math.round(currentTime),
      lane: lane
    });

    console.log(`📝 Recorded note: Lane ${lane} at ${Math.round(currentTime)}ms`);
  }

  /**
   * Generate chart JSON from recorded notes
   */
  generateChart(metadata: {
    title: string;
    artist: string;
    difficulty: string;
    bpm: number;
  }): string {
    // Sort notes by time
    const sortedNotes = [...this.recordedNotes].sort((a, b) => a.time - b.time);

    // Convert to NoteData format
    const notes: NoteData[] = sortedNotes.map(note => ({
      time: note.time,
      lane: note.lane,
      type: 'tap'
    }));

    const chart = {
      metadata: {
        title: metadata.title,
        artist: metadata.artist,
        charter: "Player Recorded",
        difficulty: metadata.difficulty,
        bpm: metadata.bpm,
        offset: 0,
        previewStart: 0
      },
      notes: notes
    };

    return JSON.stringify(chart, null, 2);
  }

  /**
   * Download chart as JSON file
   */
  downloadChart(chartJson: string, songId: string): void {
    const blob = new Blob([chartJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${songId}-chart.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`✅ Chart downloaded as ${songId}-chart.json`);
    console.log('📁 Copy this file to assets/songs/' + songId + '/chart.json to use it!');
  }

  /**
   * Check if currently recording
   */
  isCurrentlyRecording(): boolean {
    return this.isRecording;
  }

  /**
   * Get recorded note count
   */
  getRecordedCount(): number {
    return this.recordedNotes.length;
  }

  /**
   * Clear all recorded notes
   */
  reset(): void {
    this.recordedNotes = [];
    this.isRecording = false;
  }
}
